import * as storage from '../utils/LocalStorageWrapper.js';

const STORAGE_KEY = 'jollyworld-mobile-layout-v1';
export const mobileControls = {
    left: 'controls_game_left', right: 'controls_game_right', flip: 'controls_game_flip',
    brake: 'controls_game_down', accelerate: 'controls_game_up', pause: 'controls_game_pause',
    eject: 'controls_game_eject', jump: 'controls_game_jump', grab: 'controls_game_grab', action: 'controls_game_hat', dpad: 'controls_mobile_dpad',
    levelControls: 'controls_level_title',
};
export const orientationFor = (width, height) => width > height ? 'landscape' : 'portrait';
export const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

// The Freerunner is a character played with both thumbs: the directional pad under the left, jump, grab, flop and the hat
// under the right.
const modeControls = {
    vehicle: ['left', 'right', 'flip', 'brake', 'accelerate', 'eject', 'jump'],
    character: ['flip', 'dpad', 'grab'],
    runner: ['dpad', 'jump', 'grab', 'eject'],
};
export function mobileControlVisible(id, mode) {
    return modeControls[mode].includes(id) || !Object.values(modeControls).some(ids => ids.includes(id));
}

// The Freerunner's controls keep their own saved places, apart from Pause and the level launcher.
const sharedControls = ['pause', 'levelControls'];
export const layoutKey = (id, mode) => mode === 'runner' && !sharedControls.includes(id) ? `runner_${id}` : id;
export const layoutIds = [...Object.keys(mobileControls), ...Object.keys(mobileControls).filter(id => !sharedControls.includes(id)).map(id => `runner_${id}`)];

export function onFootDefault(id, bases, viewport) {
    const base = bases[id];
    const pad = {...bases.dpad, left: viewport.width < 600 ? 24 : 40,
        top: viewport.height - 24 - bases.dpad.height};
    if (id === 'dpad') return pad;
    if (id === 'flip') return {...base, left: pad.left + pad.width + 14, top: viewport.height - 24 - base.height};
    if (id === 'action' || id === 'grab') {
        const right = id === 'action' ? 30 : 94;
        const bottom = id === 'action' ? 134 : 80;
        return {...base, left: viewport.width - right - base.width, top: viewport.height - bottom - base.height};
    }
    return base;
}

// Jump in the right thumb's corner, largest; Grab beside it towards the screen's middle, Flop above Jump and the hat above
// Grab, each reached by rolling the thumb. With no room beside Jump for Grab, on a narrow phone, Grab goes above Jump and
// Flop and the hat side by side above that. Sizes are scales of each button's own base.
const RUNNER = {margin: 24, gap: 14, scale: {dpad: 1.1, jump: 1.35, grab: 1.5, eject: 1.15, action: 1.15}};
export function runnerDefault(id, bases, viewport) {
    const base = bases[id];
    const pad = onFootDefault('dpad', bases, viewport);
    if (id === 'dpad') return {...pad, scale: RUNNER.scale.dpad};
    if (!['jump', 'grab', 'eject', 'action'].includes(id)) return base;
    const size = name => bases[name].width * RUNNER.scale[name];
    const jump = {x: viewport.width - RUNNER.margin - size('jump') / 2, y: viewport.height - RUNNER.margin - size('jump') / 2};
    const beside = jump.x - size('jump') / 2 - RUNNER.gap - size('grab') >= pad.left + pad.width * RUNNER.scale.dpad + RUNNER.gap;
    let centres;
    if (beside) {
        const grab = {x: jump.x - size('jump') / 2 - RUNNER.gap - size('grab') / 2, y: jump.y - size('jump') * .3};
        centres = {
            jump, grab,
            eject: {x: jump.x + size('jump') * .05, y: jump.y - size('jump') / 2 - RUNNER.gap - size('eject') / 2},
            action: {x: grab.x - size('grab') * .15, y: grab.y - size('grab') / 2 - RUNNER.gap - size('action') / 2},
        };
    } else {
        const grab = {x: jump.x, y: jump.y - size('jump') / 2 - RUNNER.gap - size('grab') / 2};
        const eject = {x: viewport.width - RUNNER.margin - size('eject') / 2, y: grab.y - size('grab') / 2 - RUNNER.gap - size('eject') / 2};
        centres = {jump, grab, eject, action: {x: eject.x - size('eject') / 2 - RUNNER.gap - size('action') / 2, y: eject.y}};
    }
    const scale = RUNNER.scale[id];
    return {...base, left: centres[id].x - base.width * scale / 2, top: centres[id].y - base.height * scale / 2, scale};
}

export function mobileControlDefault(id, mode, bases, viewport) {
    const base = mode === 'character' ? onFootDefault(id, bases, viewport) : mode === 'runner' ? runnerDefault(id, bases, viewport) : bases[id];
    return ['pause', 'levelControls'].includes(id) ? base : {...base, left: base.left + (id === 'flip' ? 24 : 0), top: base.top - 40};
}

export function levelLauncherGeometry(pause, saved, viewport) {
    const width = 168, height = 44;
    const inset = viewport.insets || {left: 8, right: 8, top: 8, bottom: 8};
    let left = pause.left - width - 10, top = pause.top;
    if (left < inset.left) left = pause.left + pause.width + 10;
    if (left + width > viewport.width - inset.right) {
        left = pause.left;
        top = pause.top + pause.height + 8;
        if (top + height > viewport.height - inset.bottom) top = pause.top - height - 8;
    }
    return controlGeometry({left, top, width, height}, saved, viewport);
}

export function normalizeLayouts(value, ids = layoutIds) {
    const layouts = {portrait: {}, landscape: {}};
    for (const orientation of Object.keys(layouts)) for (const id of ids) {
        const saved = value?.[orientation]?.[id];
        if (!saved || ![saved.x, saved.y, saved.scale].every(Number.isFinite)) continue;
        layouts[orientation][id] = {
            x: clamp(saved.x, 0, 1), y: clamp(saved.y, 0, 1), scale: clamp(saved.scale, .75, 2),
        };
    }
    return layouts;
}

export const readMobileLayouts = () => {
    try { return normalizeLayouts(JSON.parse(storage.getItem(STORAGE_KEY))); }
    catch { return normalizeLayouts(); }
};
export const saveMobileLayouts = layouts => storage.setItem(STORAGE_KEY, JSON.stringify(normalizeLayouts(layouts)));

// Store a fraction of the available travel, keeping controls reachable when the
// browser bars, viewport or button size change. Untouched controls retain the
// original responsive layout rather than a snapshot of one screen size.
export function controlGeometry(base, saved, viewport) {
    const inset = viewport.insets || {left: 8, right: 8, top: 8, bottom: 8};
    const availableWidth = Math.max(1, viewport.width - inset.left - inset.right);
    const availableHeight = Math.max(1, viewport.height - inset.top - inset.bottom);
    const minScale = Math.max(.75, Math.min(1, 40 / Math.min(base.width, base.height)));
    const scale = Math.min(clamp(saved?.scale ?? base.scale ?? 1, minScale, 2), availableWidth / base.width, availableHeight / base.height);
    const width = base.width * scale, height = base.height * scale;
    const travelX = availableWidth - width, travelY = availableHeight - height;
    const left = saved ? inset.left + saved.x * travelX : clamp(base.left, inset.left, inset.left + travelX);
    const top = saved ? inset.top + saved.y * travelY : clamp(base.top, inset.top, inset.top + travelY);
    return {left, top, width, height, scale, x: left + width / 2, y: top + height / 2};
}

export function placeControl(base, center, scale, viewport) {
    const geometry = controlGeometry(base, {x: .5, y: .5, scale}, viewport);
    const inset = viewport.insets || {left: 8, right: 8, top: 8, bottom: 8};
    return {
        x: clamp((center.x - geometry.width / 2 - inset.left) / Math.max(1, viewport.width - inset.left - inset.right - geometry.width), 0, 1),
        y: clamp((center.y - geometry.height / 2 - inset.top) / Math.max(1, viewport.height - inset.top - inset.bottom - geometry.height), 0, 1),
        scale: geometry.scale,
    };
}

const midpoint = points => points.length > 1
    ? {x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2} : points[0];
const distance = points => Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);

export function moveControl(start, points, base, viewport) {
    const before = midpoint(start.points), after = midpoint(points);
    const ratio = points.length > 1 && distance(start.points) > 8 ? distance(points) / distance(start.points) : 1;
    const scale = controlGeometry(base, {x: .5, y: .5, scale: start.geometry.scale * ratio}, viewport).scale;
    const appliedRatio = scale / start.geometry.scale;
    return placeControl(base, {
        x: after.x + (start.geometry.x - before.x) * appliedRatio,
        y: after.y + (start.geometry.y - before.y) * appliedRatio,
    }, scale, viewport);
}
